import cookies from 'browser-cookies';
import { Box, Text } from '@stitch-fix/mode-react';
import { useSelectEvent, useViewEvent } from '@stitch-fix/event-reporter';
import { useRouter } from 'next/router';
import { AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import type { AuthenticatedClient } from '@stitch-fix/client-facing-auth';
import { recordCustomTiming } from '@stitch-fix/client-analytics-reporter';
import { Link } from '@stitch-fix/sf-next';
import { Layout, StepWrapper } from '../../../components/OnboardingNavigation';
import { useNavigationContext } from '../../../components/OnboardingNavigation/NavigationProvider';
import { RegistrationResponseCreated } from '../../../utils/requests/clientAuth/registration';
import { EmailForm } from './EmailForm';
import { NameForm } from './NameForm';
import { SignupMachineContext } from '../../../pages/signup/signup-machine';
import { Nav } from '../../../components/OnboardingNavigation/Nav';
import { SEOData } from '../../../components/SEOData';
import { BusinessLine } from './BusinessLine';
import { addBusinessLineToRumContext } from '../../../utils/loggers/browser';
import type { ResponseError } from '../../../utils/requests/clientAuth/types';
import type { SocialButtonErrorParams } from '../../../components/SocialButtons';
import type { SignUpType } from '../../../utils/requests/clientAuth/registration/types';

/**
 * RegistrationForm component with framer-motion animations via StepWrapper.
 *
 * Note: This component uses StepWrapper which expects to be rendered within
 * a MotionConfig provider (e.g., NavigationProvider) to ensure animations
 * respect user accessibility preferences for reduced motion.
 */

type EmailErrorParams = {
  type: 'email';
  data: ResponseError;
  message?: React.ReactNode;
};
export type FormRegistrationError = EmailErrorParams | SocialButtonErrorParams;
const BusinessLineStepWrapper = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    ref,
    EventProvider
  } = useViewEvent({
    schema: 'view',
    name: 'signup_panel_business_line'
  });
  return <EventProvider>
      <Box ref={ref}>{children}</Box>
    </EventProvider>;
};
const EmailStepEventWrapper = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    ref,
    EventProvider
  } = useViewEvent({
    schema: 'view',
    name: 'signup_panel_email'
  });
  return <EventProvider>
      <Box ref={ref}>{children}</Box>
    </EventProvider>;
};
const NameStepEventWrapper = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    ref,
    EventProvider
  } = useViewEvent({
    schema: 'view',
    name: 'signup_panel_client_name'
  });
  return <EventProvider>
      <Box ref={ref}>{children}</Box>
    </EventProvider>;
};
export const RegistrationForm = () => {
  const [current, send] = SignupMachineContext.useActor();
  const {
    onBackNavigation,
    onForwardNavigation
  } = useNavigationContext();
  const router = useRouter();
  const {
    reportSelectEvent
  } = useSelectEvent();
  useEffect(() => {
    recordCustomTiming('landingpages_metric_react_app_boot');
  }, []);
  const handleBack = () => {
    onBackNavigation();
    send({
      type: 'BACK'
    });
  };
  const handleLogin = () => {
    send({
      type: 'LOGIN_SUCCESS'
    });
  };
  const handleRegistration = (registrationResponse: RegistrationResponseCreated) => {
    reportSelectEvent({
      schema: 'register_user',
      name: 'registration',
      action_name: 'register_user',
      signup_type: registrationResponse.signUpType.toLowerCase() as Lowercase<SignUpType>
    });
    send({
      type: 'SIGNUP_SUCCESS',
      registrationResponse
    });
    cookies.erase('referrer_id');
  };
  const handleRegistrationError = (error: FormRegistrationError) => {
    send({
      type: 'SIGNUP_FAILURE',
      error
    });
  };
  const displayEmailStep = current.matches('email');
  const displayNameStep = current.matches('name');
  const displayBusinessLineStep = current.matches('businessLine');
  const bgColor = 'white';
  const currentStep = typeof current.value === 'string' ? current.value : Object.keys(current.value)[0];
  const canGoBack = current.can({
    type: 'BACK'
  });
  return <AnimatePresence mode="wait" initial={false}>
      <Layout key={currentStep}>
        <SEOData title="Sign Up For A Personal Stylist | Stitch Fix" canonicalUrl="https://www.stitchfix.com/signup" robots="noindex, nofollow" />

        <Nav bgColor={bgColor} backLinkHref={canGoBack ? '/signup' : undefined} onBackClick={handleBack} onLogoClick={() => {
        router.push({
          pathname: '/'
        });
      }} />

        <StepWrapper bgColor={bgColor}>
          {displayBusinessLineStep && <BusinessLineStepWrapper>
              <BusinessLine onSubmit={async businessLine => {
            addBusinessLineToRumContext(businessLine.toLowerCase() as AuthenticatedClient['businessLine']);
            send({
              type: 'NEXT',
              data: {
                businessLine
              }
            });
            onForwardNavigation();
          }} footer={<Text setting="body-small" color="blue-45" my={2} align="center">
                    Already have an account?&nbsp;
                    <Link href="/login">Sign in</Link>
                  </Text>} />
            </BusinessLineStepWrapper>}

          {displayEmailStep && <EmailStepEventWrapper>
              <EmailForm currentEmail={current.context.email} isWafEnabled={current.context.isWafEnabled} isDefaultSignupExperience={current.context.isDefaultSignupExperience} onError={handleRegistrationError} onSubmit={email => {
            onForwardNavigation();
            send({
              type: 'NEXT',
              data: email
            });
          }} socialButtonsProps={{
            mode: 'signup',
            onError: handleRegistrationError,
            onLogin: handleLogin,
            onRegistration: handleRegistration,
            businessLine: current.context.businessLine!,
            zipCode: current.context.zipCode,
            isWafEnabled: current.context.isWafEnabled,
            registrationSignupType: current.context.signupType
          }} />
            </EmailStepEventWrapper>}

          {displayNameStep && <NameStepEventWrapper>
              <NameForm businessLine={current.context.businessLine!} currentName={current.context.name?.name} email={current.context.email!} zipCode={current.context.zipCode} onSubmit={name => send({
            type: 'NEXT',
            data: {
              name
            }
          })} onRegistration={handleRegistration} onError={handleRegistrationError} isWafEnabled={current.context.isWafEnabled} registrationSignupType={current.context.signupType} />
            </NameStepEventWrapper>}
        </StepWrapper>
      </Layout>
    </AnimatePresence>;
};